import React from 'react';
import { motion } from 'framer-motion';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-rizzmas-dark via-rizzmas-dark to-rizzmas-dark" />

      {/* Subtle accent orb */}
      <motion.div
        className="absolute top-1/3 right-1/4 w-96 h-96 bg-rizzmas-gold/5 rounded-full blur-3xl"
        animate={{
          opacity: [0.2, 0.3, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Main Container with Grid Layout */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-8 grid grid-cols-1 lg:grid-cols-[1.2fr,1fr] gap-16 items-center justify-items-center">

        {/* Content Column */}
        <div className="flex flex-col items-center lg:items-end text-center lg:text-right space-y-8 order-2 lg:order-1 lg:pr-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <motion.h1
              className="text-6xl md:text-8xl lg:text-9xl font-black mb-4 bg-gradient-to-r from-rizzmas-red via-rizzmas-gold to-rizzmas-green bg-clip-text text-transparent leading-tight"
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                backgroundSize: '200% 200%',
              }}
            >
              RIZZMAS
            </motion.h1>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-rizzmas-cream"
            >
              Community Takeover
            </motion.h2>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="text-lg md:text-xl lg:text-2xl text-rizzmas-cream/80 max-w-xl"
          >
            It's not just a meme coin — it's a movement where the believers become the builders.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.1 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto lg:justify-end"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-10 py-4 bg-rizzmas-gold text-rizzmas-dark font-bold text-lg rounded-lg hover:bg-rizzmas-gold/90 transition-all duration-200"
            >
              Join the Movement
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-10 py-4 bg-transparent border-2 border-rizzmas-cream/30 text-rizzmas-cream font-semibold text-lg rounded-lg hover:border-rizzmas-cream/50 hover:bg-rizzmas-cream/5 transition-all duration-200"
            >
              View Donors
            </motion.button>
          </motion.div>
        </div>

        {/* Character Column */}
        <motion.div
          initial={{
            opacity: 0,
            x: 100,
            scale: 0.8
          }}
          animate={{
            opacity: 1,
            x: 0,
            scale: 1
          }}
          transition={{
            type: "spring",
            damping: 20,
            stiffness: 100,
            delay: 0.3
          }}
          className="flex justify-center order-1 lg:order-2 w-full lg:justify-self-start lg:pl-8"
        >
          <motion.div
            animate={{
              y: [0, -10, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="relative w-full max-w-xs lg:max-w-sm"
          >
            {/* Subtle glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-rizzmas-gold/10 to-transparent rounded-full blur-2xl" />

            {/* Character container */}
            <div className="relative">
              <img
                src="/character.jpg"
                alt="Rizzmas Character"
                className="w-full h-auto"
                style={{
                  clipPath: "circle(45% at 50% 50%)",
                  filter: "drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3)) brightness(1.05)"
                }}
              />
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <svg className="w-8 h-8 text-rizzmas-cream/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
